import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  MapPin, Plus, Pencil, Trash2, Power, ExternalLink, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({
  nome: "",
  endereco: "",
  maps_url: "",
  telefone: "",
  horario: "",
  ordem: 0,
  ativo: true,
});

function PontoFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptyForm(), ...initial } : emptyForm());
    }
  }, [open, initial]);

  const salvar = async () => {
    if (!form.nome?.trim() || !form.endereco?.trim() || !form.maps_url?.trim()) {
      toast.error("Nome, endereço e link do mapa são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/pontos-apoio/${initial.id}`, form);
        toast.success("Ponto de apoio atualizado");
      } else {
        await api.post("/pontos-apoio", form);
        toast.success("Ponto de apoio criado");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      logger.error("Falha ao salvar ponto", e);
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="dialog-ponto-form">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#083CA6]">
            <MapPin className="w-5 h-5" />
            {editing ? "Editar Ponto de Apoio" : "Novo Ponto de Apoio"}
          </DialogTitle>
          <DialogDescription>
            Preencha os dados do local físico. O link deve ser do <strong>Google Maps</strong>,
            OpenStreetMap ou Waze (começando com <code className="text-xs">https://</code>).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Nome *</Label>
            <Input
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex.: Secretaria de Habitação – Sede"
              data-testid="input-ponto-nome"
            />
          </div>
          <div>
            <Label>Endereço *</Label>
            <Input
              value={form.endereco}
              onChange={(e) => setForm({ ...form, endereco: e.target.value })}
              placeholder="Rua, nº, bairro, cidade – UF"
              data-testid="input-ponto-endereco"
            />
          </div>
          <div>
            <Label>Link do Google Maps *</Label>
            <Textarea
              value={form.maps_url}
              onChange={(e) => setForm({ ...form, maps_url: e.target.value })}
              placeholder="https://www.google.com/maps/place/..."
              rows={2}
              className="text-xs font-mono"
              data-testid="input-ponto-maps"
            />
            <p className="text-xs text-slate-500 mt-1">
              Dica: no Google Maps, abra o local → Compartilhar → Copiar link.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Telefone (opcional)</Label>
              <Input
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                placeholder="(99) 99999-9999"
                data-testid="input-ponto-telefone"
              />
            </div>
            <div>
              <Label>Ordem (menor aparece primeiro)</Label>
              <Input
                type="number"
                min={0}
                value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) || 0 })}
                data-testid="input-ponto-ordem"
              />
            </div>
          </div>
          <div>
            <Label>Horário de atendimento (opcional)</Label>
            <Input
              value={form.horario}
              onChange={(e) => setForm({ ...form, horario: e.target.value })}
              placeholder="Seg–Sex, 8h às 17h"
              data-testid="input-ponto-horario"
            />
          </div>
          <div className="flex items-center gap-3 pt-2 border-t">
            <Switch
              checked={form.ativo}
              onCheckedChange={(v) => setForm({ ...form, ativo: v })}
              id="ponto-ativo-switch"
              data-testid="switch-ponto-ativo"
            />
            <Label htmlFor="ponto-ativo-switch" className="text-sm">
              {form.ativo ? "Ativo (visível na home)" : "Inativo (não aparece na home)"}
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={salvar}
            disabled={saving}
            className="bg-[#083CA6] hover:bg-[#062f85]"
            data-testid="btn-save-ponto"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editing ? "Salvar alterações" : "Criar ponto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeletePontoDialog({ target, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const confirmar = async () => {
    if (!target) return;
    setDeleting(true);
    try {
      await api.delete(`/pontos-apoio/${target.id}`);
      toast.success("Ponto excluído");
      onDeleted?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir");
    } finally { setDeleting(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-ponto-delete">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" /> Excluir Ponto de Apoio
          </DialogTitle>
          <DialogDescription>
            O ponto <strong>{target?.nome}</strong> será removido permanentemente.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancelar</Button>
          <Button variant="destructive" onClick={confirmar} disabled={deleting}
                  data-testid="btn-confirm-delete-ponto">
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PontosApoioPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/pontos-apoio");
      setItems(r.data.items || []);
    } catch (e) {
      logger.error("Falha ao listar pontos", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const abrirNovo = () => { setEditTarget(null); setFormOpen(true); };
  const abrirEdicao = (p) => { setEditTarget(p); setFormOpen(true); };

  const toggleAtivo = async (p) => {
    try {
      const r = await api.put(`/pontos-apoio/${p.id}/toggle`);
      toast.success(r.data.ativo ? "Ponto ativado" : "Ponto desativado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao alternar");
    }
  };

  return (
    <div className="space-y-6" data-testid="pontos-apoio-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-2">
            <MapPin className="w-7 h-7" /> Pontos de Apoio
          </h1>
          <p className="text-sm text-slate-600">
            Locais físicos de atendimento presencial exibidos na página inicial.
          </p>
        </div>
        <Button onClick={abrirNovo} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-novo-ponto">
          <Plus className="w-4 h-4 mr-2" /> Novo Ponto de Apoio
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lista ({items.length})</CardTitle>
          <CardDescription>Pontos inativos ficam ocultos na home mas permanecem salvos.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {(() => {
            if (loading) {
              return (
                <div className="p-6 text-center text-slate-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Carregando...
                </div>
              );
            }
            if (items.length === 0) {
              return (
                <div className="p-10 text-center">
                  <MapPin className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <h3 className="font-bold mb-1">Nenhum ponto cadastrado</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Cadastre a Secretaria de Habitação, postos descentralizados etc.
                  </p>
                  <Button onClick={abrirNovo} className="bg-[#083CA6] hover:bg-[#062f85]">
                    <Plus className="w-4 h-4 mr-2" />Criar primeiro ponto
                  </Button>
                </div>
              );
            }
            return (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left">
                  <tr>
                    <th className="p-3">Ordem</th>
                    <th className="p-3">Nome / Endereço</th>
                    <th className="p-3">Contato</th>
                    <th className="p-3">Status</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, i) => (
                    <tr key={p.id} className={i % 2 ? "bg-slate-50" : ""} data-testid={`row-ponto-${p.id}`}>
                      <td className="p-3 font-mono text-xs">{p.ordem}</td>
                      <td className="p-3">
                        <div className="font-semibold">{p.nome}</div>
                        <div className="text-xs text-slate-500">{p.endereco}</div>
                      </td>
                      <td className="p-3 text-xs">
                        {p.telefone && <div>{p.telefone}</div>}
                        {p.horario && <div className="text-slate-500">{p.horario}</div>}
                        {!p.telefone && !p.horario && <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-3">
                        <Badge className={p.ativo ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-700"}>
                          {p.ativo ? "Ativo" : "Inativo"}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <a href={p.maps_url} target="_blank" rel="noopener noreferrer" title="Abrir no mapa">
                            <Button size="sm" variant="outline">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          </a>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => abrirEdicao(p)}
                            data-testid={`btn-edit-ponto-${p.id}`}
                            title="Editar"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleAtivo(p)}
                            data-testid={`btn-toggle-ponto-${p.id}`}
                            title={p.ativo ? "Desativar" : "Ativar"}
                          >
                            <Power className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-600 hover:text-white"
                            onClick={() => setDelTarget(p)}
                            data-testid={`btn-delete-ponto-${p.id}`}
                            title="Excluir"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </CardContent>
      </Card>

      <PontoFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editTarget}
        onSaved={load}
      />
      <DeletePontoDialog
        target={delTarget}
        onClose={() => setDelTarget(null)}
        onDeleted={load}
      />
    </div>
  );
}
